if (!customElements.get("ac-pricing-calculator")) {
  class AcPricingCalculator extends HTMLElement {
    constructor() {
      super();
      this._updateResults = this._updateResults.bind(this);
    }
    connectedCallback() {
      this._tiers = JSON.parse(this.getAttribute("tiers"));
      this._tiers.sort(([minA], [minB]) => minB - minA);
      this._productContainers = Array.from(this.querySelectorAll("[data-product-price]"));
      this._productCountInputs = this._productContainers.map((pc) => pc.querySelector("ac-number-input"));
      this._productResults = this._productContainers.map(() => ({
        costPerCamera: 0,
        serviceCostPerCamera: 0,
        count: 0
      }));
      this._totalsContainer = this.querySelector('[data-group="totals"]');
      this._productCountInputs.forEach((countInput) => {
        countInput.addEventListener("change", this._updateResults);
      });
      this._numberFormatter = Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        trailingZeroDisplay: "stripIfInteger",
        roundingMode: "ceil"
      });
      this._updateResults();
    }
    _updateResults() {
      const totalCount = this._getTotalCount();
      this._productContainers.forEach((_, index) => {
        this._updateProductResults(index, totalCount);
      });
      this._updateTotals();
    }
    _updateProductResults(productIndex, totalCount) {
      const productContainer = this._productContainers[productIndex];
      const appliesDiscount = Boolean(productContainer.dataset.applyDiscount);
      const discountTier = this._getDiscountTier(totalCount);
      const costPerCamera = Math.ceil(
        parseInt(productContainer.dataset.productPrice, 10) * (appliesDiscount ? discountTier.cameraFactor : 1)
      );
      const hasServiceProduct = Boolean(productContainer.dataset.servicePrice);
      const serviceCostPerCamera = hasServiceProduct ? this._getServicePlanCost(parseInt(productContainer.dataset.servicePrice, 10)) : 0;
      const cameraCount = parseInt(this._productCountInputs[productIndex].value, 10);
      const cameraTotal = costPerCamera * cameraCount;
      const monthlyTotal = serviceCostPerCamera * cameraCount;
      const costPerCameraEl = productContainer.querySelector('[data-value="cost-per-camera"]');
      costPerCameraEl.textContent = this._formatPrice(costPerCamera);
      if (serviceCostPerCamera) {
        const serviceCostPerCameraEl = productContainer.querySelector('[data-value="service-cost-per-camera"]');
        serviceCostPerCameraEl.textContent = this._formatPrice(serviceCostPerCamera);
      }
      const cameraTotalEl = productContainer.querySelector('[data-value="camera-total"]');
      if (cameraTotalEl) {
        cameraTotalEl.textContent = this._formatPrice(cameraTotal);
      }
      if (serviceCostPerCamera) {
        const monthlyTotalEl = productContainer.querySelector('[data-value="monthly-total"]');
        if (monthlyTotalEl) {
          monthlyTotalEl.textContent = this._formatPrice(monthlyTotal);
        }
      }
      this._productResults[productIndex] = {
        costPerCamera,
        serviceCostPerCamera,
        cameraCount,
        cameraTotal,
        monthlyTotal
      };
    }
    _updateTotals() {
      if (this._totalsContainer) {
        const totals = this._productResults.reduce(
          (rTotal, result) => {
            rTotal.cameraTotal += result.cameraTotal;
            rTotal.monthlyTotal += result.monthlyTotal;
            rTotal.orMT = result.monthlyTotal;
            return rTotal;
          },
          { cameraTotal: 0, monthlyTotal: 0 }
        );
        const cameraTotalEl = this._totalsContainer.querySelector('[data-value="camera-total"]');
        if (cameraTotalEl) cameraTotalEl.textContent = this._formatPrice(totals.cameraTotal);
        const monthlyTotalEl = this._totalsContainer.querySelector('[data-value="monthly-total"]');
        if (monthlyTotalEl) monthlyTotalEl.textContent = this._formatPrice(totals.monthlyTotal);
        this._productCountInputs.forEach((countInput, index) => {
          const inputEls = this._totalsContainer.querySelectorAll(`[data-input-value="product${index}.cameraCount"]`);
          inputEls.forEach((inputEl) => {
            inputEl.value = countInput.value;
          });
        });
      }
    }
    _getDiscountTier(count) {
      const [_, discountValue, serviceDiscountValue] = this._tiers.find(([minCount]) => count >= minCount);
      return { cameraFactor: (100 - discountValue) / 100, serviceFactor: (100 - serviceDiscountValue) / 100 };
    }
    _getServicePlanCost(basePrice) {
      const servicePlanCount = this._productContainers.reduce((sum, productContainer, productIndex) => {
        const cameraCount = parseInt(this._productCountInputs[productIndex].value, 10);
        const hasServiceProduct = Boolean(productContainer.dataset.servicePrice);
        return sum + (hasServiceProduct ? cameraCount : 0);
      }, 0);
      return Math.ceil(basePrice * this._getDiscountTier(servicePlanCount).serviceFactor);
    }
    _getTotalCount() {
      return this._productCountInputs.reduce((sum, countInput) => sum + parseInt(countInput.value, 10), 0);
    }
    _formatPrice(number) {
      return `${this._numberFormatter.format(number / 100)}`;
    }
  }
  customElements.define("ac-pricing-calculator", AcPricingCalculator);
}
